// Review & apply drawer: shows every question on a request with the value the
// org had when it was sent vs the manufacturer's answer. Nothing touches an
// assessment until the user applies — apply writes the selected answers
// server-side and marks them applied.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const FONT = "'Raleway', 'SF Pro Display', sans-serif";

// Mirrors the skip reasons returned by mergeManufacturerAnswers in
// server/routes/manufacturerRequests.js.
const SKIP_REASONS = {
  not_held: "the manufacturer said they don't hold it",
  no_document: 'no certificate was uploaded',
  part_missing: 'the part they refer to no longer exists',
  invalid_weight: 'the weight was not a usable number',
  empty: 'they were empty',
  unknown_certification: 'the certification is not recognised',
  unknown_key: 'the question type is not recognised',
};

// Whether an answer actually carries something the apply step can write.
// Certifications are the odd one out: "we don't hold this" and a bare claim on
// a standard that needs proof are answers, but there's nothing to publish.
function isApplicable(q) {
  if (q.key !== 'certification') return true;
  const v = q?.response?.value;
  if (!v || typeof v !== 'object' || v.status === 'not_held') return false;
  return !!v.file?.path || q.context?.requires_file === false;
}

export default function ReviewDrawer({ requestId, supplierId, authHeaders, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveAssessments, setLiveAssessments] = useState({});
  const [checked, setChecked] = useState(() => new Set());
  const [applying, setApplying] = useState(null); // itemId while applying
  const [applyError, setApplyError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API_BASE_URL}/manufacturer-requests/${requestId}`, { headers });
      const d = res.data?.data;
      setDetail(d);
      setError(null);
      // Default-select every answered, not-yet-applied question that has
      // something to apply.
      const next = new Set();
      for (const item of d?.items || []) {
        for (const q of item.questions || []) {
          if (q?.response?.answered_at && !q?.applied?.at && isApplicable(q)) next.add(q.id);
        }
      }
      setChecked(next);
      // Live assessments for "changed since sent" hints.
      const pairs = await Promise.all(
        (d?.items || []).map(async (item) => {
          try {
            const res2 = await axios.get(`${API_BASE_URL}/lca/assessments`, {
              params: { product_id: item.product_id, supplier_id: supplierId },
              headers,
            });
            return [item.product_id, res2.data?.data || null];
          } catch {
            return [item.product_id, null];
          }
        })
      );
      setLiveAssessments(Object.fromEntries(pairs));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [requestId, supplierId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const applyItem = async (item) => {
    const ids = (item.questions || [])
      .filter((q) => checked.has(q.id) && q?.response?.answered_at && !q?.applied?.at && isApplicable(q))
      .map((q) => q.id);
    if (ids.length === 0) return;
    setApplying(item.id);
    setApplyError(null);
    try {
      const headers = await authHeaders();
      const res = await axios.post(
        `${API_BASE_URL}/manufacturer-requests/${requestId}/items/${item.id}/apply`,
        { question_ids: ids },
        { headers }
      );
      const updated = res.data?.data?.questions;
      if (Array.isArray(updated)) {
        setDetail((prev) => ({
          ...prev,
          items: prev.items.map((it) => (it.id === item.id ? { ...it, questions: updated } : it)),
        }));
      }
      const skipped = res.data?.data?.skipped || [];
      if (skipped.length > 0) {
        const reasons = [...new Set(skipped.map((s) => SKIP_REASONS[s.reason] || 'they were empty'))];
        setApplyError(
          `${skipped.length} answer${skipped.length === 1 ? ' was' : 's were'} skipped — ${reasons.join('; ')}.`
        );
      }
      onChanged?.();
    } catch (e) {
      setApplyError(e?.response?.data?.error || 'Failed to apply answers');
    } finally {
      setApplying(null);
    }
  };

  const copyLink = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(`${detail.portal_url}\nAccess code: ${detail.access_code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  const counts = useMemo(() => {
    let answered = 0;
    let applied = 0;
    let total = 0;
    for (const item of detail?.items || []) {
      for (const q of item.questions || []) {
        total += 1;
        if (q?.response?.answered_at) answered += 1;
        if (q?.applied?.at) applied += 1;
      }
    }
    return { answered, applied, total };
  }, [detail]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 2000 }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(760px, 100vw)',
          background: '#f8f9fb',
          zIndex: 2001,
          overflowY: 'auto',
          fontFamily: FONT,
          boxShadow: '-8px 0 30px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '16px 20px', position: 'sticky', top: 0, zIndex: 5 }}>
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div>
              <div className="text-uppercase small fw-bold" style={{ color: '#6b7280', letterSpacing: 0.5, fontSize: 11 }}>
                Manufacturer request
              </div>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 17 }}>
                {detail?.manufacturer_name || detail?.manufacturer_email || 'Loading…'}
              </div>
              {detail && (
                <div style={{ color: '#9ca3af', fontSize: 12 }}>
                  {detail.manufacturer_email} · {counts.answered}/{counts.total} answered · {counts.applied} applied
                </div>
              )}
            </div>
            <div className="d-flex gap-2 align-items-center">
              {detail && (
                <button
                  className="btn btn-sm"
                  onClick={copyLink}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, background: copied ? '#ecfdf5' : '#fff', color: copied ? '#047857' : '#374151' }}
                >
                  {copied ? '✓ Copied' : 'Copy link + code'}
                </button>
              )}
              <button className="btn-close" onClick={onClose} />
            </div>
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {loading && !detail && <div className="text-center py-5" style={{ color: '#9ca3af' }}>Loading…</div>}
          {error && <div className="text-center py-5" style={{ color: '#b91c1c' }}>{error}</div>}

          {detail?.message && (
            <div style={{ background: '#fff', borderLeft: '3px solid #111827', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#374151', marginBottom: 16 }}>
              Your message: “{detail.message}”
            </div>
          )}

          {applyError && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
              {applyError}
            </div>
          )}

          {(detail?.items || []).map((item) => {
            const applicable = (item.questions || []).filter((q) => q?.response?.answered_at && !q?.applied?.at && isApplicable(q));
            const checkedCount = applicable.filter((q) => checked.has(q.id)).length;
            const live = liveAssessments[item.product_id];
            return (
              <div key={item.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', background: '#fcfcfd' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{item.product_name_snapshot}</div>
                    <div style={{ color: '#9ca3af', fontSize: 12 }}>{item.product_code_snapshot || '—'}</div>
                  </div>
                  {applicable.length > 0 && (
                    <button
                      className="btn btn-sm"
                      disabled={applying === item.id || checkedCount === 0}
                      onClick={() => applyItem(item)}
                      style={{ background: '#111827', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, opacity: applying === item.id || checkedCount === 0 ? 0.6 : 1, whiteSpace: 'nowrap' }}
                    >
                      {applying === item.id ? 'Applying…' : `Apply selected (${checkedCount})`}
                    </button>
                  )}
                </div>

                {(item.questions || []).map((q) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    checked={checked.has(q.id)}
                    onToggle={() => toggle(q.id)}
                    drifted={hasDrifted(q, live)}
                  />
                ))}
              </div>
            );
          })}

          {detail && counts.answered === 0 && (
            <div className="text-center" style={{ color: '#6b7280', fontSize: 13, padding: '8px 0 24px' }}>
              No answers yet — the manufacturer hasn't filled anything in.
              {detail.status === 'sent' && ' They may not have opened the link; try a reminder.'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---------- Row ----------

function QuestionRow({ question: q, checked, onToggle, drifted }) {
  const answered = !!q?.response?.answered_at;
  const applied = !!q?.applied?.at;
  const applicable = isApplicable(q);
  return (
    <div className="d-flex gap-3" style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb' }}>
      <div style={{ paddingTop: 2 }}>
        {applied ? (
          <span title="Applied" style={{ color: '#047857', fontSize: 15 }}>✓</span>
        ) : answered && applicable ? (
          <input type="checkbox" checked={checked} onChange={onToggle} />
        ) : (
          <span
            title={answered && !applicable ? 'Nothing to apply from this answer' : undefined}
            style={{ color: '#d1d5db', fontSize: 15 }}
          >
            ·
          </span>
        )}
      </div>
      <div style={{ minWidth: 0, flexGrow: 1 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{q.label}</span>
          {applied && (
            <span style={{ background: '#ecfdf5', color: '#047857', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>APPLIED</span>
          )}
          {!answered && (
            <span style={{ background: '#f3f4f6', color: '#9ca3af', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>UNANSWERED</span>
          )}
          {drifted && !applied && (
            <span
              title="Your assessment has changed since this request was sent — double-check before applying."
              style={{ background: '#fef9c3', color: '#a16207', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}
            >
              ⚠ CHANGED SINCE SENT
            </span>
          )}
        </div>
        <div className="row g-2 mt-1">
          <div className="col-md-6">
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>You had</div>
            <div style={{ fontSize: 13, color: '#6b7280', wordBreak: 'break-word' }}>{summarizeContext(q) || <em>nothing recorded</em>}</div>
          </div>
          <div className="col-md-6">
            <div style={{ fontSize: 10, fontWeight: 700, color: answered ? '#047857' : '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>They answered</div>
            <div style={{ fontSize: 13, color: answered ? '#111827' : '#c7cdd6', fontWeight: answered ? 500 : 400, wordBreak: 'break-word' }}>
              {answered ? summarizeAnswer(q) || <em>empty</em> : '—'}
            </div>
            {answered && q.key === 'certification' && q.response?.value?.file?.path && (
              <a
                href={`${API_BASE_URL}/cert-files?path=${encodeURIComponent(q.response.value.file.path)}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, color: '#4338ca', fontWeight: 600 }}
              >
                View document ↗
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Summaries ----------

function partLine(p) {
  return [p?.weight_grams != null && p?.weight_grams !== '' ? `${p.weight_grams}g` : null, p?.material_subcategory || p?.material_category || null, p?.material_quality || null, p?.process_name ? `(${p.process_name})` : null]
    .filter(Boolean)
    .join(' ');
}
function partsSummary(parts) {
  if (!Array.isArray(parts) || parts.length === 0) return '';
  return parts.map(partLine).filter(Boolean).join(' · ') || `${parts.length} part(s)`;
}
function packagingSummary(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  return rows.map((r) => [r?.type, r?.weight != null && r?.weight !== '' ? `${r.weight}g` : null].filter(Boolean).join(' ')).filter(Boolean).join(' · ');
}
function legsSummary(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return '';
  return legs
    .map((l) => [`${l?.origin_country || '?'} → ${l?.destination_country || '?'}`, l?.mode, l?.distance_km != null && l?.distance_km !== '' ? `${l.distance_km}km` : null].filter(Boolean).join(' '))
    .join(' · ');
}
function manufacturingSummary(v) {
  if (!v || typeof v !== 'object') return '';
  return [
    v.assembly_location,
    v.energy_source,
    v.manufacturing_energy_consumption_kwh != null && v.manufacturing_energy_consumption_kwh !== '' ? `${v.manufacturing_energy_consumption_kwh} kWh` : null,
    v.renewable_percentage != null && v.renewable_percentage !== '' ? `${v.renewable_percentage}% renewable` : null,
  ].filter(Boolean).join(' · ');
}

function summarizeByKey(key, v) {
  if (v == null) return '';
  switch (key) {
    case 'weight': {
      const w = typeof v === 'object' ? v.weight_grams : v;
      return w != null && w !== '' ? `${w} g` : '';
    }
    case 'country_of_origin':
      return (typeof v === 'object' ? v.country_of_origin : v) || '';
    case 'parts':
      return partsSummary(Array.isArray(v) ? v : v.parts);
    case 'part_field':
      return String((typeof v === 'object' && 'value' in v ? v.value : v) ?? '');
    case 'packaging':
      return packagingSummary(Array.isArray(v) ? v : v.packaging_materials);
    case 'manufacturing':
      return manufacturingSummary(v);
    case 'transport':
      return legsSummary(Array.isArray(v) ? v : v.transport_legs);
    case 'certification': {
      if (typeof v !== 'object') return '';
      if (v.status === 'not_held') return ['Not held', v.note ? `“${v.note}”` : null].filter(Boolean).join(' — ');
      return [
        v.file?.name || 'Confirmed — no document',
        v.reference ? `#${v.reference}` : null,
        v.valid_until ? `valid until ${v.valid_until}` : null,
        v.note ? `“${v.note}”` : null,
      ].filter(Boolean).join(' · ');
    }
    case 'custom':
      return typeof v === 'object' ? v.text || '' : String(v);
    default:
      return '';
  }
}

function summarizeAnswer(q) {
  return summarizeByKey(q.key, q?.response?.value);
}

function summarizeContext(q) {
  const ctx = q?.context || {};
  if (q.key === 'certification') {
    if (!ctx.already_on_file) return '';
    return ctx.existing_document_name ? `On file: ${ctx.existing_document_name}` : 'On file';
  }
  if (q.key === 'part_field') {
    const part = ctx.part ? partLine(ctx.part) : null;
    const cur = ctx.current_value ? String(ctx.current_value) : '';
    return [part ? `Part: ${part}` : null, cur ? `Current: ${cur}` : null].filter(Boolean).join(' — ');
  }
  return summarizeByKey(q.key, ctx);
}

// Mirror of the server's context builder over the LIVE assessment, so we can
// hint when data moved under an in-flight request.
function hasDrifted(q, live) {
  if (!q?.context || live === undefined) return false;
  if (!live) return false;
  const liveCtx = (() => {
    switch (q.key) {
      case 'weight': return { weight_grams: live.weight_grams ?? null };
      case 'parts': return { parts: Array.isArray(live.parts) ? live.parts : [] };
      case 'part_field': {
        const parts = Array.isArray(live.parts) ? live.parts : [];
        const part = Number.isInteger(q.part_index) ? parts[q.part_index] || null : null;
        return { part_index: q.part_index, field: q.field, part, current_value: part ? part[q.field] ?? null : null };
      }
      case 'packaging': return { packaging_materials: Array.isArray(live.packaging_materials) ? live.packaging_materials : [] };
      case 'manufacturing':
        return {
          assembly_location: live.assembly_location ?? null,
          energy_source: live.energy_source ?? null,
          manufacturing_energy_consumption_kwh: live.manufacturing_energy_consumption_kwh ?? null,
          renewable_percentage: live.renewable_percentage ?? null,
        };
      case 'transport': return { transport_legs: Array.isArray(live.transport_legs) ? live.transport_legs : [] };
      case 'certification': {
        const raw = live.social_impact;
        const si = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
        const existing = si?.certFiles?.[q.cert_id] || null;
        const held = Array.isArray(si?.product) && si.product.includes(q.cert_id);
        return {
          ...q.context,
          already_on_file: held && !!existing,
          existing_document_name: existing?.name || null,
        };
      }
      default: return null;
    }
  })();
  if (liveCtx == null) return false;
  try {
    return JSON.stringify(liveCtx) !== JSON.stringify(q.context);
  } catch {
    return false;
  }
}
